import { motion, type Easing } from "framer-motion";
import { Helmet } from "react-helmet-async"; // Assuming you have react-helmet-async installed for SEO

import {
  FaMobileAlt,
  FaPalette,
  FaRocket,
  FaCode,
  FaCrosshairs,
  FaShieldAlt,
  FaCogs,
  FaLightbulb,
  FaDollarSign,
  FaHeadset,
  FaQuestionCircle,
} from "react-icons/fa"; // Added more icons
import { Link } from "react-router-dom";

// Animation variants
const fadeInLeft = {
  hidden: { opacity: 0, x: -80 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
};

const fadeInRight = {
  hidden: { opacity: 0, x: 80 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};

const fadeInUp = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const fadeProcessStep = (index: number) => ({
  hidden: { opacity: 0, x: index % 2 === 0 ? -50 : 50 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.7,
      ease: [0, 0, 0.58, 1] as Easing,
    },
  },
});

const FlutterAppDevelopment = () => {
  return (
    <>
      <Helmet>
        <title>Expert Flutter App Development in Noida | Nadezhda Venture</title>
        <meta
          name="description"
          content="Get high-performance, beautiful cross-platform Flutter app development services in Noida. We build scalable and feature-rich Android, iOS, and web apps with a single codebase."
        />
        <meta
          name="keywords"
          content="Flutter app development, cross-platform app, Android app, iOS app, web app, mobile app development Noida, Flutter experts, app development company"
        />
        <meta name="author" content="Nadezhda Venture" />

        {/* Open Graph for social sharing */}
        <meta property="og:title" content="Expert Flutter App Development in Noida | Nadezhda Venture" />
        <meta property="og:description" content="Build your next innovative mobile app with Flutter for Android, iOS, and web. Fast, beautiful, and efficient." />
        <meta property="og:image" content="https://www.franticpro.com/resource/newHeader/images/flutter-app-developer.png" />
        <meta property="og:url" content="https://yourdomain.com/flutter-app-development" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* SECTION 1 - Hero Section with Transparent Image and Text Overlay */}
      <section className="relative overflow-hidden flex items-center justify-center h-[400px] md:h-[400px] lg:h-[400px]">
        {/* Transparent Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://png.pngtree.com/thumb_back/fh260/background/20190223/ourmid/pngtree-blue-fluttering-technology-background-boardbackground-image_83398.jpg" // Using a Flutter-related background image
            alt="Flutter Transparent Background"
            className="w-full h-full object-cover opacity-80" // Adjust opacity for desired transparency
          />
          {/* Optional: Add a subtle overlay for better text readability and darker background */}
          <div className="absolute inset-0 bg-black opacity-70"></div> {/* Adjust opacity as needed */}
        </div>

        {/* Text and Image Content */}
        <div className="min-h-screen flex flex-col md:flex-row items-center justify-center gap-12 px-6 py-20 max-w-7xl mx-auto relative z-10">
          <motion.div
            className="flex-1 text-center md:text-left"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInLeft}
          >
            <h1 className="text-3xl md:text-4xl font-extrabold text-orange-600 mb-6">
              Cross-Platform Brilliance with Flutter
            </h1>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Deliver sleek, high-performance mobile apps with a single codebase. Our Flutter experts build fast, beautiful apps for Android, iOS, and the web — all from one framework.
            </p>
            
            <Link to="/contact">
  <button className="bg-orange-700 hover:bg-orange-500 px-6 py-3 rounded-lg font-semibold transition text-white">
    Let's Build Your App
  </button>
</Link>
          </motion.div>

          {/* Changed this section back to a distinct image if that was the intent, or keep as a div with background */}
          {/* If the user wants a separate image (not background of the section) in this flex item, uncomment this: */}
          <motion.div
            className="flex-1 w-full max-w-2xl flex justify-center items-center" // Added flex to center the image if needed
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInRight}
          >
            <img
              src="https://www.franticpro.com/resource/newHeader/images/flutter-app-developer.png"
              alt="Flutter App Showcase"
              className="rounded-xl shadow-lg w-full h-auto object-contain max-h-[400px]" // Use object-contain to ensure image fits
            />
          </motion.div>
        </div>
      </section>


      
        {/* SECTION 3 - Why Partner With Us for Flutter Development? (New Section) */}
        <section className="bg-white text-blue-600 py-20 px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
              Why Partner With Us for <span className="text-orange-500">Flutter Development </span>?
            </h2>
            <div className="grid md:grid-cols-3 gap-10 text-center">
              {[
                {
                  icon: <FaCrosshairs size={50} className="text-orange-400 mb-4 mx-auto" />,
                  title: "Expertise & Experience",
                  description: "Our team comprises seasoned Flutter developers with a proven track record of delivering successful, high-quality applications across various industries.",
                },
                {
                  icon: <FaCode size={50} className="text-blue-400 mb-4 mx-auto" />,
                  title: "Single Codebase Efficiency",
                  description: "Leverage Flutter's single codebase advantage to launch your app on Android, iOS, and web simultaneously, reducing development time and costs.",
                },
                {
                  icon: <FaPalette size={50} className="text-purple-400 mb-4 mx-auto" />,
                  title: "Stunning UI/UX Design",
                  description: "We craft visually appealing and highly intuitive user interfaces, ensuring an exceptional user experience that captivates your audience.",
                },
                {
                  icon: <FaRocket size={50} className="text-green-400 mb-4 mx-auto" />,
                  title: "Optimized Performance",
                  description: "Our Flutter apps are built for speed and responsiveness, providing a smooth, lag-free experience comparable to native applications.",
                },
                {
                  icon: <FaShieldAlt size={50} className="text-red-400 mb-4 mx-auto" />,
                  title: "Robust & Scalable Solutions",
                  description: "We build future-proof Flutter applications with clean architecture, making them easy to maintain, update, and scale as your business grows.",
                },
                {
                  icon: <FaHeadset size={50} className="text-yellow-400 mb-4 mx-auto" />,
                  title: "Dedicated Support",
                  description: "From concept to post-launch maintenance, we provide comprehensive support and clear communication throughout the entire development lifecycle.",
                },
              ].map((feature, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                  transition={{ delay: i * 0.15 }}
                  className="bg-blue-950 p-8 rounded-xl shadow-lg flex flex-col items-center hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300"
                >
                  {feature.icon}
                  <h3 className="text-xl font-semibold mb-3 mt-2 text-white">{feature.title}</h3>
                  <p className="text-gray-300 leading-relaxed">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        

        {/* SECTION 4 - Our Flutter App Development Process (New Section) */}
        <section className="bg-white text-blue-900 py-20 px-6">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-12">
              Our Agile <span className="text-blue-600">Flutter Development Process</span>
            </h2>
            <div className="space-y-16">
              {[
                {
                  
                  title: "Discovery & Planning",
                  description: "We start by deeply understanding your app idea, target audience, and business goals to define the project scope, features, and technology stack.",
                  image: "https://img.freepik.com/free-photo/group-diverse-people-having-business-meeting_53876-25060.jpg?size=626&ext=jpg&ga=GA1.1.18264147.1710129600&semt=ais", // Placeholder, find relevant image
                },
               
                {
                  
                  title: "Development & Iteration",
                  description: "Our Flutter developers bring the design to life using a clean code approach. We follow agile methodologies, providing regular updates and incorporating feedback.",
                  image: "https://images.businessnewsdaily.com/app/uploads/2022/04/04074408/Software_Developer_Getty_RossHelen.jpg", // Placeholder, find relevant image
                },
               
                {
                 
                  title: "Deployment & Launch",
                  description: "We handle the entire deployment process, submitting your Flutter app to Google Play Store and Apple App Store, ensuring a smooth and successful launch.",
                  image: "https://www.pagetraffic.in/wp-content/uploads/2022/12/site-migration-process-1024x683.jpg", // Placeholder, find relevant image
                },
                
              ].map((item, i) => (
                <motion.div
                  key={i}
                  className={`flex flex-col md:flex-row items-center gap-8 md:gap-12 p-8 rounded-lg shadow-xl ${
                    i % 2 === 0 ? "bg-white" : "bg-gray-850" // Slightly different dark tones
                  }`}
                  initial="hidden"
                  whileInView="visible"
                  variants={fadeProcessStep(i)}
                  viewport={{ once: true, amount: 0.1 }}
                >
                  <div className={`md:w-1/2 ${i % 2 === 0 ? "md:order-1" : "md:order-2"}`}>
                    <img src={item.image} alt={item.title} className="rounded-lg shadow-2xl w-full h-auto object-cover max-h-[300px]" />
                  </div>
                  <div className={`md:w-1/2 text-left ${i % 2 === 0 ? "md:order-2" : "md:order-1"}`}>
                    
                    <h3 className="text-2xl md:text-3xl font-bold mb-4 leading-tight">{item.title}</h3>
                    <p className="text-orange-600 leading-relaxed text-xl ">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 5 - What We Offer (Comprehensive Flutter Services) (New Section) */}
        <section className="bg-gray-200 text-white py-20 px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-blue-800">
              Our Comprehensive <span className="text-orange-600">Flutter Development Services</span>
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: <FaMobileAlt size={30} className="text-orange-500" />,
                  title: "Custom Mobile App Development",
                  description: "Building bespoke Android and iOS applications from the ground up tailored to your unique business needs using Flutter.",
                },
                {
                  icon: <FaCode size={30} className="text-blue-500" />,
                  title: "Flutter Web Application Development",
                  description: "Extending your mobile presence to the web with high-performance, responsive Flutter web applications for seamless user experience.",
                },
                {
                  icon: <FaPalette size={30} className="text-purple-500" />,
                  title: "UI/UX Design for Flutter Apps",
                  description: "Crafting visually appealing and intuitive user interfaces specifically optimized for Flutter's expressive UI capabilities.",
                },
                {
                  icon: <FaCogs size={30} className="text-green-500" />,
                  title: "Flutter Migration & Upgradation",
                  description: "Migrating existing apps to Flutter or upgrading your current Flutter applications to the latest versions for enhanced performance and features.",
                },
                {
                  icon: <FaRocket size={30} className="text-red-500" />,
                  title: "Performance Optimization",
                  description: "Optimizing your Flutter application for maximum speed, responsiveness, and efficiency, ensuring a smooth user experience.",
                },
                {
                  icon: <FaDollarSign size={30} className="text-yellow-500" />,
                  title: "Cost-Effective Solutions",
                  description: "Leveraging Flutter's single codebase to provide efficient development, reducing time-to-market and overall project costs.",
                },
              ].map((service, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                  transition={{ delay: i * 0.1 }}
                  className="bg-blue-950 p-6 rounded-lg shadow-md flex items-start space-x-4 hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="flex-shrink-0">{service.icon}</div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-orange-600">{service.title}</h3>
                    <p className="text-gray-300 text-base">{service.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

       
        {/* SECTION 7 - Showcase Section (Existing) */}
        <section className="bg-white px-6 py-24"> {/* Changed to a darker gray for continuity */}
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center gap-12 rounded-3xl overflow-hidden">
            {/* Left: Mobile Mockups */}
            <motion.div
              className="flex-1 flex justify-center"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInLeft}
            >
              <img
                src="https://www.xongolab.com/wp-content/uploads/2025/02/ott-platform-development.png"
                alt="Flutter Mobile Mockups"
                className="max-w-full md:max-w-[90%] h-auto"
              />
            </motion.div>

            {/* Right: Content */}
            <motion.div
              className="flex-1 text-center md:text-left"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInRight}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-blue-700 mb-6">
                Real-World <span className="text-orange-500">Flutter Applications</span>
              </h2>
              <p className="text-gray-700 text-lg mb-6">
                Experience the future of mobile apps with our creations on Flutter. Meticulously developed
                for optimal performance, our apps boast sleek designs, seamless navigation, and
                cross-platform compatibility. Get digital experience with cutting-edge technology brought to
                life through our Flutter-powered applications.
              </p>
<Link to="/contact">
  <button className="bg-orange-700 hover:bg-orange-500 px-6 py-3 rounded-lg font-semibold transition text-white">
    Let's Build Your App
  </button>
</Link>
            </motion.div>
          </div>
        </section>


         {/* SECTION 6 - Why Flutter is the Future? (New Section) */}
        <section className="bg-white text-blue-600 py-20 px-6">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-12">
              Why Flutter is Paving the Way for <span className="text-orange-400">Future Apps</span>
            </h2>
            <div className="grid md:grid-cols-3 gap-10">
              {[
                {
                  icon: <FaLightbulb size={50} className="text-orange-400 mb-4 mx-auto" />,
                  title: "Hot Reload & Rapid Development",
                  description: "Flutter's Hot Reload feature allows for instant UI updates, drastically speeding up development cycles and enabling quick iterations.",
                },
                {
                  icon: <FaMobileAlt size={50} className="text-blue-400 mb-4 mx-auto" />,
                  title: "Native Performance & UI",
                  description: "Despite being cross-platform, Flutter compiles to native code, delivering near-native performance and allowing for highly customizable, expressive UIs.",
                },
                {
                  icon: <FaCode size={50} className="text-green-400 mb-4 mx-auto" />,
                  title: "Growing Ecosystem & Community",
                  description: "Backed by Google, Flutter boasts a rapidly expanding ecosystem, rich libraries, and a vibrant global community providing robust support.",
                },
              ].map((reason, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                  transition={{ delay: i * 0.15 }}
                  className="bg-blue-950 p-8 rounded-xl shadow-lg flex flex-col items-center hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300"
                >
                  {reason.icon}
                  <h3 className="text-xl font-semibold mb-3 mt-2 text-white">{reason.title}</h3>
                  <p className="text-gray-300 leading-relaxed">{reason.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>


        {/* SECTION 8 - Client Testimonials (New Section) */}
        {/* <section className="bg-white text-blue-600 py-20 px-6">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-12">
              Hear From Our Happy <span className="text-orange-500">Flutter Clients</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-10">
              {[
                {
                  quote: "Nadezhda Venture's Flutter team delivered an exceptional mobile app for our startup. Their professionalism, technical skill, and dedication were outstanding. The app runs flawlessly on both Android and iOS!",
                  author: "Ananya Sharma",
                  company: "Startup Founder",
                },
                {
                  quote: "We needed a fast, visually appealing app for our e-commerce platform, and Flutter was the perfect choice. Nadezhda Venture brought our vision to life with pixel-perfect UI and incredible performance. Highly recommend!",
                  author: "Rahul Verma",
                  company: "E-commerce Lead",
                },
              ].map((testimonial, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                  transition={{ delay: i * 0.2 }}
                  className="bg-gray-900 p-8 rounded-lg shadow-xl text-left border-l-4 border-orange-500 hover:shadow-2xl transition-shadow duration-300"
                >
                  <p className="text-lg italic mb-6 text-gray-200">"{testimonial.quote}"</p>
                  <p className="font-semibold text-orange-400">{testimonial.author}</p>
                  <p className="text-gray-400 text-sm">{testimonial.company}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section> */}

        {/* SECTION 9 - Frequently Asked Questions (FAQ) (New Section) */}
        <section className="bg-white to-blue-600 text-blue-600 py-20 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
              Common Questions About <span className="text-orange-400">Flutter App Development</span>
            </h2>
            <div className="space-y-6">
              {[
                {
                  question: "Why choose Flutter over native Android/iOS development?",
                  answer: "Flutter offers significant advantages like a single codebase for multiple platforms (Android, iOS, web), faster development cycles with Hot Reload, expressive UI capabilities, and near-native performance, leading to cost and time savings.",
                },
                {
                  question: "How long does it take to develop a Flutter app?",
                  answer: "The timeline depends on the app's complexity, features, and design. A simple app might take 4-8 weeks, while a more complex application could take 3-6 months or more. We provide a detailed estimate after understanding your requirements.",
                },
                {
                  question: "Will my Flutter app have native performance and look?",
                  answer: "Yes, Flutter compiles directly to native ARM code, ensuring excellent performance. Its extensive widget library allows for highly customizable UIs that can perfectly mimic native platform designs or create unique brand-specific looks.",
                },
                {
                  question: "Do you provide post-launch support and maintenance?",
                  answer: "Absolutely. We offer comprehensive post-launch support, including bug fixes, performance monitoring, security updates, and feature enhancements to ensure your Flutter app remains robust and up-to-date.",
                },
              ].map((faq, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.1 }}
                  variants={fadeInUp}
                  transition={{ delay: i * 0.1 }}
                  className="bg-gray-300 p-6 rounded-lg shadow-md"
                >
                  <details className="group">
                    <summary className="flex justify-between items-center cursor-pointer text-lg font-semibold text-black">
                      {faq.question}
                      <FaQuestionCircle className="text-orange-400 group-open:rotate-180 transition-transform duration-300" />
                    </summary>
                    <p className="mt-4 text-gray-900 leading-relaxed">{faq.answer}</p>
                  </details>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      {/* Moved the closing section tag to the end to enclose all content */}
      </>
    );
};

export default FlutterAppDevelopment;